import React, { useState } from "react";
import styled from "styled-components";
import { useXarrow } from "react-xarrows";
import { ExpressionEditorValueFirst } from "../../../CommonUI/DataEditing/ExpressionEditorValueFirst";
import { EditGrayIcon } from "../../../CommonUI/Icon/EditGrayIcon";
import { DeleteGrayIcon } from "../../../CommonUI/Icon/DeleteGrayIcon";

import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { ExpressionConfiguration } from "../../../rule-engine-core/Expressions/ExpressionConfiguration";

import { StyledButton } from "./EvaluationNode";

import { TreeRuleNodeProps } from "./XTreeRuleNode";
import { Error } from "../../Table/CommonUI/Error";
import { Connector } from "./Connector";

import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import {
    ExpressionReactContext,
    ExpressionRenderType,
    expressionSettings,
} from "../../../Expressions/Structure/ExpressionReactContext";
import { ExpressionTestResultColorContext } from "../../../Expressions/Testing/ExpressionTestResultColorContext";
import { TestingConstants } from "../../../CommonUI/Testing/TestingConstants";
import { ConfirmDialog } from "../../../CommonUI/ConfirmDialog";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { TreeRuleConstant } from "../../../rule-engine-core/Rule/Tree/Common/TreeRuleConstant";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants"; // eslint-disable-next-line @typescript-eslint/no-unused-vars

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ResultRuleNode = ({ node, ruleDefinition, treeRuleBuilder }: TreeRuleNodeProps) => {
    const testContext = React.useContext(TestContext);

    const updateXarrow = useXarrow();

    const [openEditLayer, setOpenEditLayer] = useState<boolean>(false);

    const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

    const onDelete = () => {
        if (
            node.expression.isEmpty ||
            DataUtilities.getCookie(TreeRuleConstant.TREE_RULE_CLEAR_COOKIE_KEY) === "true"
        ) {
            node.delete();
            StateManager.changed(node);
            StateManager.changed(ruleDefinition);
        } else {
            setOpenConfirmDialog(true);
        }
    };

    const onEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setOpenEditLayer(true);
    };

    if (testContext) {
        const currentTestItem = testContext.findItem(node.expression._id);

        let testcolor: string | undefined;

        if (currentTestItem) {
            if (currentTestItem.result === "true") {
                testcolor = TestingConstants.TEXT_SUCCESS;
            } else {
                testcolor = TestingConstants.TEXT_FAIL;
            }
        }

        return (
            <ResultRuleNodeRender
                id={node._id}
                isTestPassed={!!currentTestItem}
            >
                <ValueOrExpressionEditorRender color={node.color}>
                    <ExpressionTestResultColorContext.Provider value={testcolor}>
                        <ExpressionReactContext.Provider value={expressionSettings[ExpressionRenderType.TEXT]}>
                            <ExpressionEditorValueFirst
                                expression={node.expression}
                                onUpdate={updateXarrow}
                            />
                        </ExpressionReactContext.Provider>
                    </ExpressionTestResultColorContext.Provider>
                </ValueOrExpressionEditorRender>
                <Error issues={node.getDesignValidationIssues()} />
                <Connector nodeid={node._id} />
            </ResultRuleNodeRender>
        );
    }

    return (
        <ResultRuleNodeRender id={node._id}>
            <ObserveState
                listenTo={ruleDefinition}
                control={() => {
                    return (
                        <>
                            <ValueOrExpressionEditorRender color={node.color}>
                                <ExpressionEditorValueFirst
                                    expression={node.expression}
                                    onUpdate={(newValue) => {
                                        if (newValue instanceof ExpressionConfiguration) {
                                        } else {
                                            node.expression.expressionRoot.value = newValue;
                                        }
                                        StateManager.propertyChanged(node, "expression");
                                    }}
                                    open={openEditLayer}
                                    onClose={() => setOpenEditLayer(false)}
                                />
                            </ValueOrExpressionEditorRender>
                            <Error issues={node.getDesignValidationIssues()} />
                        </>
                    );
                }}
            />

            <Connector nodeid={node._id} />

            <div className="Actions-group">
                <StyledButton onClick={onEdit}>
                    <EditGrayIcon />
                </StyledButton>
                {ruleDefinition.root !== node && (
                    <StyledButton onClick={onDelete}>
                        <DeleteGrayIcon />
                    </StyledButton>
                )}
            </div>
            <ConfirmDialog
                open={openConfirmDialog}
                onConfirm={(dontAskAgain) => {
                    if (dontAskAgain) {
                        DataUtilities.setCookieValue(TreeRuleConstant.TREE_RULE_CLEAR_COOKIE_KEY, "true");
                    }

                    node.delete();
                    StateManager.changed(node);
                    StateManager.changed(ruleDefinition);
                    setOpenConfirmDialog(false);
                }}
                onCancel={() => setOpenConfirmDialog(false)}
            />
        </ResultRuleNodeRender>
    );
};

const ResultRuleNodeRender = styled.div<{ isTestPassed?: boolean; isError?: boolean }>`
    position: relative;

    ${(props) =>
        props.isError
            ? `
                border: 2px solid ${TestingConstants.BORDER_FAIL};
                background: ${TestingConstants.BACKGROUND_FAIL};
            `
            : `border: 2px solid ${props.isTestPassed ? TestingConstants.BORDER_PASSED : "transparent"};`}

    padding: 4px;

    border-radius: 4px;

    &:hover {
        .Actions-group {
            opacity: 1;
        }
    }

    .Actions-group {
        opacity: 0;

        transition: opacity 0.1s ease-in;

        background: ${UIStyleConstants.WHITE_COLOR};
        border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
        box-sizing: border-box;
        border-radius: 4px;

        display: flex;
        align-items: center;

        position: absolute;

        right: 0;
        top: -30px;
    }
`;

const ValueOrExpressionEditorRender = styled.div<{ color: string }>`
    background: ${(props) => props.color};

    border-radius: 4px;
    border-radius: 4px;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;

    .ValueEditor-root {
        width: 100%;
        padding: 6px 4px;

        .ValueEditor-display {
            padding: 6px 12px;
        }
    }
`;
